import { classNames } from "@/utility/jsx";
import { MmFabButton } from "@mm/zeus-react";
import { Duration } from "luxon";
import React, { useMemo } from "react";
import styles from "./style.module.scss";

function leadingZero(value: number) {
  if (!Number.isNaN(value) && `${value}`.length === 1) {
    return `0${value.toString()}`;
  }

  return value.toString();
}

type TimeInputProps = {
  duration?: Duration;
  onChange?: (time: Duration) => void;
  minHours?: number;
  maxHours?: number;
  resetPosition?: "left" | "right";
};

export const DurationInput = ({ duration, onChange, minHours, maxHours, resetPosition = "right" }: TimeInputProps) => {
  const { minutes, hours } = useMemo(() => {
    if (!duration) {
      return {
        minutes: 0,
        hours: 0,
      };
    }

    const { minutes, hours } = duration.shiftTo("hours", "minutes").toObject();
    return {
      minutes: minutes || 0,
      hours: hours || 0,
    };
  }, [duration]);

  const handleClearOffset = () => {
    onChange?.(Duration.fromObject({ hours: 0, minutes: 0 }));
  };

  return (
    <div className={styles["input-wrapper"]}>
      {resetPosition === "left" && (
        <div>
          <MmFabButton
            onClick={handleClearOffset}
            size={"xsmall"}
            rounded={true}
            iconname={"refresh"}
            data-testid={"clear-offset"}
          />
        </div>
      )}
      <div>
        <input
          data-testid="offset-time-input-hours"
          type="number"
          min={minHours}
          max={maxHours}
          step={1}
          value={hours.toString()}
          onChange={(event) => {
            if (event.target.checkValidity()) {
              const hours = Number.parseInt(event.target.value.length ? event.target.value : "0");
              onChange?.(Duration.fromObject({ hours, minutes }));
            }
          }}
          className={classNames(styles["time-input"], styles["time-input__hours"])}
        />
        <div>h</div>
      </div>
      <div>
        <input
          data-testid="offset-time-input-minutes"
          type="number"
          min={-59}
          max={59}
          step={1}
          value={leadingZero(minutes)}
          onChange={(event) => {
            if (event.target.checkValidity()) {
              const minutes = Number.parseInt(event.target.value.length ? event.target.value : "0");
              onChange?.(Duration.fromObject({ hours, minutes }));
            }
          }}
          className={classNames(styles["time-input"], styles["time-input__minutes"])}
        />
        <div>min</div>
      </div>
      {resetPosition === "right" && (
        <div>
          <MmFabButton
            onClick={handleClearOffset}
            size={"xsmall"}
            rounded={true}
            iconname={"refresh"}
            data-testid={"clear-offset"}
          />
        </div>
      )}
    </div>
  );
};
